import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";

import { Col, Row, Button, Spinner } from "reactstrap";
import DataTable from "../../shared/base-components/DataTable";
import { useTodoListMutation } from "./Todo.actions";
import { Personel } from "./Todo.constants";
import TodoModal from "./TodoModal";
import { MODAL_TYPES, Permissions } from "../../shared/models/Types";
import TodoDeleteModal from "./TodoDeleteModal";
import { formatDateString } from "../../shared/utils/formatText";
import { useRecoilValue } from "recoil";
import { permissionsSelector } from "../../shared/actions/Authentication.actions";
import TodoDetailModal from "./TodoDetailModal";

const TodoList = () => {
  const permissionList = useRecoilValue(permissionsSelector);
  const isMobile: boolean = /iPhone|iPad|iPod|Android/i.test(
    navigator.userAgent
  );

  const { data, mutate, isLoading } = useTodoListMutation();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalOptions, setModalOptions] = useState<{
    data?: Personel;
    modalType?: string;
  }>({
    data: undefined,
    modalType: undefined,
  });

  const toggle = () => {
    setIsModalOpen(!isModalOpen);
  };

  const modalOptionsHandler = (data?: Personel, modalType?: string) => {
    setModalOptions({ data: data, modalType: modalType });
  };

  const addPermission = permissionList[Permissions.AddTodo];
  const updatePermission = permissionList[Permissions.ChangeTodo];
  const viewPermission = permissionList[Permissions.ViewTodo];
  const deletePermission = permissionList[Permissions.DeleteTodo];

  const columns = useMemo(
    () => [
      {
        Header: "İş Adı",
        filterable: false,
        disableFilters: true,
        accessor: "name",
        Cell: (response: any) => (
          <>
            <p className="text mb-0">{response.row.original?.name}</p>
          </>
        ),
      },
      {
        Header: "Açıklama",
        accessor: "description",
        filterable: false,
        disableFilters: true,
        Cell: (response: any) => {
          return (
            <p className="text mb-0">{response.row.original?.description}</p>
          );
        },
      },
      {
        Header: "Son Tarih",
        accessor: "date",
        filterable: false,
        disableFilters: true,
        Cell: (response: any) => (
          <>
            {
              <p className="text mb-0">
                {formatDateString(response.row.original?.date)}
              </p>
            }
          </>
        ),
      },
      {
        Header: "Atanan Kişi",
        accessor: "person_who_assign",
        filterable: false,
        disableFilters: true,
        Cell: (user: any) => (
          <>
            <p className="text mb-0">
              {user.row.original?.person_who_assign == null
                ? "Herkese "
                : user.row.original?.person_who_assign?.fullname}
            </p>
          </>
        ),
      },
      {
        Header: "Tamamlayan Kişi",
        accessor: "person_who_complete2",
        filterable: false,
        disableFilters: true,
        Cell: (user: any) => (
          <>
            <p className="text mb-0">
              {user.row.original?.person_who_complete?.fullname}
            </p>
          </>
        ),
      },
      {
        Header: "Oluşturma Tarihi",
        accessor: "created_at",
        filterable: false,
        disableFilters: true,
        Cell: (response: any) => (
          <>
            {
              <p className="text mb-0">
                {formatDateString(response.row.original?.created_at)}
              </p>
            }
          </>
        ),
      },
      {
        Header: "Durum",
        accessor: "person_who_complete",
        filterable: true,
        sortable: true,
        disableFilters: true,
        Cell: (response: any) => {
          const personWhoComplete = response.row.original.person_who_complete;
          const isCompleted = !!personWhoComplete;
          const color = isCompleted ? "success" : "warning";
          return (
            <div
              className={
                "badge bg-" + color + "-subtle text-" + color + " font-size-12"
              }
            >
              {isCompleted ? "Tamamlandı" : "Devam Ediyor"}
            </div>
          );
        },
      },
      {
        Header: "İşlemler",
        Cell: (cellProps: any) => {
          const personWhoComplete = cellProps.row.original.person_who_complete;
          const isCompleted = !!personWhoComplete;

          return (
            <div
              className={
                !isCompleted
                  ? "d-flex gap-3"
                  : "d-flex gap-1 justify-content-start"
              }
            >
              {!isCompleted && updatePermission && (
                <Link className="text-warning" to="#">
                  <i
                    className="mdi mdi-pencil font-size-18"
                    id="edittooltip"
                    onClick={() => {
                      const userData = cellProps.row.original;

                      toggle();
                      modalOptionsHandler(userData, MODAL_TYPES.UPDATE);
                    }}
                  ></i>
                </Link>
              )}

              {deletePermission && (
                <Link className="text-danger" to="#">
                  <i
                    className="mdi mdi-delete font-size-18"
                    id="deletetooltip"
                    onClick={() => {
                      const userData = cellProps.row.original;

                      toggle();
                      modalOptionsHandler(userData, MODAL_TYPES.DELETE);
                    }}
                  ></i>
                </Link>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  const mobileColumns = useMemo(
    () => [
      {
        Header: "İş Adı/Son Tarih",
        filterable: false,
        disableFilters: true,
        accessor: "fullname",
        Cell: (response: any) => (
          <>
            <h5 className="font-size-14 mb-1">
              {response.row.original.name}

              <p className="font-size-13 text-muted mt-2">
                {formatDateString(response.row.original?.date)}
              </p>
            </h5>
          </>
        ),
      },
      {
        Header: "Durum",
        accessor: "person_who_complete",
        filterable: true,
        sortable: true,
        disableFilters: true,
        Cell: (response: any) => {
          const personWhoComplete = response.row.original.person_who_complete;
          const isCompleted = !!personWhoComplete;
          const color = isCompleted ? "success" : "warning";
          return (
            <div
              className={
                "badge bg-" + color + "-subtle text-" + color + " font-size-12"
              }
            >
              {isCompleted ? "Tamamlandı" : "Devam Ediyor"}
            </div>
          );
        },
      },
      {
        Header: "İşlemler",
        accessor: "phone_number",
        disableFilters: true,
        Cell: (cellProps: any) => {
          return (
            <div className="d-flex gap-3">
              {updatePermission && (
                <Link className="text-warning" to="#">
                  <i
                    className="mdi mdi-pencil font-size-18"
                    id="edittooltip"
                    onClick={() => {
                      const userData = cellProps.row.original;

                      toggle();
                      modalOptionsHandler(userData, MODAL_TYPES.UPDATE);
                    }}
                  ></i>
                </Link>
              )}
              <Link className="text-success" to="#">
                <i
                  className="mdi mdi-eye font-size-18"
                  id="deletetooltip"
                  onClick={() => {
                    const userData = cellProps.row.original;

                    toggle();
                    modalOptionsHandler(userData, MODAL_TYPES.DETAIL);
                  }}
                ></i>
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  useEffect(() => {
    mutate({});
  }, [mutate]);

  return (
    <React.Fragment>
      <Row className="align-items-center">
        <Col md={6}>
          <div className="mb-3">
            <h5 className="card-title">
              Yapılacak İş Listesi
              <span className="text-muted fw-normal ms-2">
                {isLoading ? (
                  <Spinner className="ms-1" color="primary" />
                ) : (
                  `(${data?.length})`
                )}
              </span>
            </h5>
          </div>
        </Col>

        <Col md={6}>
          <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3">
            <div>
              <ul className="nav nav-pills"></ul>
            </div>
            <div>
              {addPermission && (
                <Button
                  onClick={() => {
                    toggle();
                    modalOptionsHandler(undefined, MODAL_TYPES.CREATE);
                  }}
                  className="btn btn-light"
                >
                  <i className="bx bx-plus me-1"></i> İş Ekle
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg="12">
          {data?.length && viewPermission ? (
            <DataTable
              columns={isMobile ? mobileColumns : columns}
              data={data}
              isGlobalFilter={true}
              isAddUserList={true}
              customPageSize={10}
              className="table align-middle datatable dt-responsive table-check nowrap"
            />
          ) : (
            !isLoading && <>Herhangi bir iş bulunmamaktadır.</>
          )}
        </Col>
      </Row>

      {isModalOpen &&
        updatePermission &&
        (modalOptions.modalType === MODAL_TYPES.UPDATE ||
          modalOptions.modalType === MODAL_TYPES.CREATE) && (
          <TodoModal
            mutate={mutate}
            userData={modalOptions.data}
            modalType={modalOptions.modalType}
            isModalOpen={isModalOpen}
            toggle={toggle}
          />
        )}

      {isModalOpen &&
        deletePermission &&
        modalOptions.modalType === MODAL_TYPES.DELETE && (
          <TodoDeleteModal
            mutate={mutate}
            userData={modalOptions.data}
            modalType={modalOptions.modalType}
            isModalOpen={isModalOpen}
            toggle={toggle}
          />
        )}

      {isModalOpen &&
        deletePermission &&
        modalOptions.modalType === MODAL_TYPES.DETAIL && (
          <TodoDetailModal
            userData={modalOptions.data}
            modalType={modalOptions.modalType}
            isModalOpen={isModalOpen}
            toggle={toggle}
          />
        )}
    </React.Fragment>
  );
};

export default TodoList;
